import * as React from 'react'

import { useFilePreview } from '~/client/src/shared/contexts/FilePreviewContext'
import { openAttachmentPreview } from '~/client/src/shared/utils/filePreviewUtils'

import AttachmentsPreviewStrip from '../FilesInput/AttachmentsPreviewStrip'
import {
  Action,
  Body,
  Details,
  Header,
  IResourceCardA11yProps,
  Leading,
  Meta,
  PopupShell,
  ResourceCardVariant,
  Root,
  Selection,
  StandardContent,
  Status,
  Subtitle,
  Title,
  Trailing,
  useResourceCard,
} from './ResourceCardParts'
import { IResourceCardModel } from './types'

export { useResourceCard }

const MediaSlot: React.FC<{ leading?: React.ReactNode }> = ({ leading }) => {
  const model = useResourceCard()
  const { showPreview } = useFilePreview()

  const onPreview = React.useCallback(
    (
      attachments: Parameters<typeof openAttachmentPreview>[0],
      index: number,
    ) => {
      openAttachmentPreview(attachments, index, showPreview)
    },
    [showPreview],
  )

  if (!model.attachments?.length) return null
  const content = (
    <AttachmentsPreviewStrip
      attachments={model.attachments}
      maxPreviews={3}
      onPreview={onPreview}
    />
  )
  return leading ? <div className="tw:pl-8">{content}</div> : content
}

const Standard: React.FC<
  {
    model: IResourceCardModel
    leading?: React.ReactNode
    selection?: React.ReactNode
    trailing?: React.ReactNode
    // Under the subtitle, inside the card: part of what a click on it picks.
    footer?: React.ReactNode
    onClick?: () => void
    variant?: ResourceCardVariant
    className?: string
  } & IResourceCardA11yProps
> = ({
  model,
  leading,
  selection,
  trailing,
  footer,
  onClick,
  variant,
  className,
  role,
  ariaSelected,
  ariaDisabled,
}) => (
  <Root
    model={model}
    onClick={onClick}
    variant={variant}
    className={className}
    role={role}
    ariaSelected={ariaSelected}
    ariaDisabled={ariaDisabled}
  >
    {selection && <Selection>{selection}</Selection>}
    {model.attachments?.length ? (
      <div className="tw:flex tw:flex-col tw:gap-2 tw:flex-1 tw:min-w-0">
        <StandardContent
          leading={leading}
          trailing={trailing}
          footer={footer}
        />
        <MediaSlot leading={leading} />
      </div>
    ) : (
      <StandardContent leading={leading} trailing={trailing} footer={footer} />
    )}
  </Root>
)

const PopupStandard: React.FC<{
  model: IResourceCardModel
  leading?: React.ReactNode
  onClose: () => void
  onClick?: () => void
  className?: string
}> = ({ model, leading, onClose, onClick, className }) => (
  <PopupShell
    model={model}
    onClose={onClose}
    onClick={onClick}
    className={className}
  >
    {model.attachments?.length ? (
      <div className="tw:flex tw:flex-col tw:gap-2">
        <StandardContent leading={leading} />
        <MediaSlot leading={leading} />
      </div>
    ) : (
      <StandardContent leading={leading} />
    )}
  </PopupShell>
)

export const ResourceCard = {
  Root,
  PopupShell,
  Standard,
  PopupStandard,
  Selection,
  Trailing,
  Leading,
  Body,
  Details,
  Header,
  Title,
  Subtitle,
  Status,
  Meta,
  Action,
}
